import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { useIntl } from "react-intl";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { ProgressSpinner } from "primereact/progressspinner";
import { RadioButton } from "primereact/radiobutton";
import { SelectButton } from "primereact/selectbutton";
import { toast } from "react-toastify";
import { MultiSelectInput } from "../../common/fields";
import UserFilters from "../users/filters/UserFilters";
import * as CertificateBatchConstants from "../../../constants/CertificateBatchConstants";
import * as GlobalConstants from "../../../constants/GlobalConstants";
import {
  canStart,
  describeUserFilters,
  isMultiItem,
  isTooMany,
  toCertificateBatchRequest,
  toItemOptions,
} from "../../../helpers/CertificateBatchHelper";
import {
  certificateBatchAudiencesRequest,
  certificateBatchCreateRequest,
  certificateBatchItemsRequest,
  certificateBatchPreviewRequest,
  certificateBatchPreviewReset,
} from "../../../store/slices/certificateBatchSlice";
import "./certificate-batch-dialog.scss";

const noop = () => {};
// Stable defaults: the effects below depend on these props.
const NO_FILTERS = [];
const NO_ITEMS = [];
const NO_AUDIENCES = [];
// The preview waits until the choices have settled: All training counts every
// certified item for every learner, so a burst of clicks must not send a
// burst of full previews (the API finishes each one even when the UI has
// moved on).
export const PREVIEW_DELAY_MS = 400;

// One section of the dialog: every section has the same heading.
const Section = ({ id, title, children }) => (
  <section
    className="certificate-batch-section flex flex-column gap-3"
    aria-labelledby={id}
  >
    <h3 id={id} className="certificate-batch-heading">
      {title}
    </h3>
    {children}
  </section>
);

Section.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  children: PropTypes.node,
};

// The only way to start a certificate download: "Download certificates" on
// the Certificate downloads page. Nothing is preselected; the learners and
// the training type (default: all training types, the client's "anything
// else they may have taken") are chosen here.
export const CertificateBatchDialog = ({
  visible,
  items = NO_ITEMS,
  itemsType = null,
  itemsRequesting = false,
  audiences = NO_AUDIENCES,
  audiencesRequesting = false,
  preview = null,
  previewRequesting = false,
  previewError = null,
  creating = false,
  createError = null,
  onHide,
  loadItems = noop,
  loadAudiences = noop,
  loadPreview = noop,
  resetPreview = noop,
  createBatch = noop,
}) => {
  const intl = useIntl();
  const message = (id, values) => intl.formatMessage({ id }, values);
  const [allTraining, setAllTraining] = useState(false);
  const [chosen, setChosen] = useState(NO_ITEMS);
  const [chosenAudiences, setChosenAudiences] = useState(NO_AUDIENCES);
  const [completedFrom, setCompletedFrom] = useState(null);
  const [completedTo, setCompletedTo] = useState(null);
  const [userFilters, setUserFilters] = useState(NO_FILTERS);
  const [trainingType, setTrainingType] = useState(
    GlobalConstants.TRAINING_TYPE_ALL
  );
  // The body of the last preview sent (JSON), so an unchanged body is not sent again.
  const [sentJson, setSentJson] = useState(null);
  // The body of the last Prepare download (JSON): its refusal shows until a choice changes.
  const [preparedJson, setPreparedJson] = useState(null);
  const wasCreating = useRef(creating);
  // Set by Prepare download, cleared by every opening: only a create started
  // in the opening on screen may close the dialog or show its refusal in the
  // footer. A create started before a Cancel can still be running when the
  // dialog opens again (C-BUG-1).
  const preparedHere = useRef(false);

  // The store keeps the last list loaded (and restores it after a reload):
  // only a list of every item type, the one this dialog asks for, is offered.
  const allItems =
    itemsType === CertificateBatchConstants.CERTIFICATE_ITEM_TYPE_ALL
      ? items
      : NO_ITEMS;
  const options = useMemo(
    () =>
      toItemOptions(allItems, (item) =>
        intl.formatMessage(
          { id: "certificate_batch_item_option" },
          {
            name: item.name,
            type: intl.formatMessage({
              id:
                CertificateBatchConstants.CERTIFICATE_ITEM_TYPE_LABELS[
                  item.itemType
                ] || "learning_program",
            }),
          }
        )
      ),
    [allItems, intl]
  );
  const audienceOptions = useMemo(
    () =>
      audiences.map((audience) => ({ id: audience.id, name: audience.name })),
    [audiences]
  );
  const trainingTypeOptions = useMemo(
    () =>
      GlobalConstants.REPORT_TRAINING_TYPES.map((type) => ({
        label: intl.formatMessage({ id: type.id }),
        value: type.value,
      })),
    [intl]
  );

  // Every opening starts clean: the store is persisted, so an old preview
  // must not show.
  useEffect(() => {
    if (visible) {
      setAllTraining(false);
      setChosen(NO_ITEMS);
      setChosenAudiences(NO_AUDIENCES);
      setCompletedFrom(null);
      setCompletedTo(null);
      setUserFilters(NO_FILTERS);
      setTrainingType(GlobalConstants.TRAINING_TYPE_ALL);
      setSentJson(null);
      setPreparedJson(null);
      preparedHere.current = false;
      resetPreview();
      loadItems({
        itemType: CertificateBatchConstants.CERTIFICATE_ITEM_TYPE_ALL,
      });
      loadAudiences();
    }
  }, [visible, loadItems, loadAudiences, resetPreview]);

  // The body of both the preview and the create request.
  const request = useMemo(
    () =>
      allTraining || chosen.length > 0
        ? toCertificateBatchRequest({
            items: chosen,
            allTraining,
            completedFrom,
            completedTo,
            userFilters,
            audiences: chosenAudiences,
            trainingType,
          })
        : null,
    [
      allTraining,
      chosen,
      completedFrom,
      completedTo,
      userFilters,
      chosenAudiences,
      trainingType,
    ]
  );

  // Compared as JSON: "Add filter" without a value, or a change undone,
  // builds a new object with the same body.
  const requestJson = useMemo(
    () => (request ? JSON.stringify(request) : null),
    [request]
  );
  const previewPending = Boolean(requestJson) && requestJson !== sentJson;

  useEffect(() => {
    if (!visible || !previewPending) {
      return undefined;
    }
    const timer = setTimeout(() => {
      setSentJson(requestJson);
      loadPreview(request);
    }, PREVIEW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [visible, previewPending, requestJson, request, loadPreview]);

  // The dialog stays open until the API has created the batch: it closes on
  // success, and on a refusal keeps every choice and says why in the footer.
  const prepare = () => {
    setPreparedJson(requestJson);
    preparedHere.current = true;
    createBatch(request);
  };

  const createErrorMessageId =
    (createError &&
      (CertificateBatchConstants.CERTIFICATE_BATCH_ERROR_MESSAGES[createError] ||
        "something_else_wrong")) ||
    null;

  useEffect(() => {
    if (wasCreating.current && !creating) {
      const startedInThisOpening = preparedHere.current && visible;
      preparedHere.current = false;
      if (startedInThisOpening) {
        if (!createErrorMessageId) {
          onHide();
        }
      } else if (createErrorMessageId) {
        // Cancelled (or opened again) while the API was still answering: the
        // footer on screen is not about that request, so say it here.
        toast.error(intl.formatMessage({ id: createErrorMessageId }));
      }
    }
    wasCreating.current = creating;
  }, [creating, createErrorMessageId, visible, onHide, intl]);

  const createErrorId =
    (!creating &&
      preparedJson !== null &&
      preparedJson === requestJson &&
      createErrorMessageId) ||
    null;

  const userFilterLines = describeUserFilters(userFilters);
  const multiItem = isMultiItem(allTraining, chosen);
  const noItems = !itemsRequesting && options.length === 0;

  const errorId =
    (previewError &&
      CertificateBatchConstants.CERTIFICATE_BATCH_ERROR_MESSAGES[
        previewError
      ]) ||
    // All training on a tenant where no program or learning object has a
    // certificate yet (the API counts no items and says so).
    (preview &&
      allTraining &&
      !preview.hasCertificate &&
      "certificate_batch_no_items") ||
    (preview &&
      !preview.hasCertificate &&
      "certificate_batch_error_certificate_not_set") ||
    (preview &&
      preview.count === 0 &&
      "certificate_batch_error_no_certificates") ||
    (preview &&
      isTooMany(preview.count) &&
      "certificate_batch_error_too_many") ||
    null;

  // A create started before a Cancel is still running: Prepare download stays
  // busy until it is answered (one batch at a time), and the footer says why,
  // so the spinner is not read as the new choices being prepared (review M6).
  const earlierCreateRunning = creating && !preparedHere.current;

  // The footer's left side: what Prepare download would give.
  const renderSummary = () => {
    if (!request) {
      return (
        <span className="certificate-batch-hint">
          {message("certificate_batch_choose_item")}
        </span>
      );
    }
    if (createErrorId) {
      return <span className="text-red-600">{message(createErrorId)}</span>;
    }
    if (previewRequesting || previewPending) {
      return (
        <span className="flex align-items-center gap-2">
          <ProgressSpinner
            style={{ width: "1.25rem", height: "1.25rem" }}
            strokeWidth="6"
          />
          <span>{message("certificate_batch_counting")}</span>
        </span>
      );
    }
    if (errorId) {
      return <span className="text-red-600">{message(errorId)}</span>;
    }
    if (!preview) {
      return null;
    }
    const details = [
      typeof preview.learnerCount === "number" &&
        message("certificate_batch_learner_count", {
          count: preview.learnerCount,
        }),
      multiItem &&
        preview.itemCount > 0 &&
        message("certificate_batch_from_items", { count: preview.itemCount }),
      preview.skipped > 0 &&
        message("certificate_batch_skipped", { count: preview.skipped }),
    ].filter(Boolean);
    return (
      <span className="flex flex-column">
        <span className="certificate-batch-count">
          {message("certificate_batch_count", { count: preview.count })}
        </span>
        {details.map((line) => (
          <span key={line} className="certificate-batch-hint">
            {line}
          </span>
        ))}
      </span>
    );
  };

  const footer = (
    <div className="certificate-batch-footer">
      <div className="certificate-batch-summary" aria-live="polite">
        {renderSummary()}
        {(earlierCreateRunning && (
          <span className="certificate-batch-hint block mt-1">
            {message("certificate_batch_earlier_preparing")}
          </span>
        )) ||
          null}
      </div>
      <div className="certificate-batch-actions">
        <Button
          type="button"
          label={message("cancel")}
          severity="secondary"
          outlined
          onClick={onHide}
        />
        <Button
          type="button"
          label={message("certificate_batch_prepare")}
          icon="pi pi-download"
          loading={creating}
          disabled={
            !request ||
            previewPending ||
            previewRequesting ||
            creating ||
            !canStart(preview)
          }
          onClick={prepare}
        />
      </div>
    </div>
  );

  const renderDate = (inputId, labelId, value, onChange, range) => (
    <div className="edexlms-field flex-1">
      <label htmlFor={inputId} className="edexlms-field-label block">
        {message(labelId)}
      </label>
      <div className="certificate-batch-date">
        <Calendar
          inputId={inputId}
          value={value}
          onChange={(e) => onChange(e.value || null)}
          {...range}
          dateFormat="dd M yy"
          showButtonBar
          className="w-full"
        />
        <i
          className="pi pi-calendar certificate-batch-date-icon"
          aria-hidden="true"
        />
      </div>
    </div>
  );

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header={message("certificate_batch_dialog_title")}
      footer={footer}
      className="certificate-batch-dialog"
      style={{ width: "40rem" }}
      breakpoints={{ "640px": "calc(100vw - 2rem)" }}
    >
      {(visible && (
        <div className="flex flex-column gap-4">
          <p className="certificate-batch-hint m-0">
            {message("certificate_batch_intro")}
          </p>

          <Section
            id="certificateBatchTrainingHeading"
            title={message("certificate_batch_training")}
          >
            <div className="certificate-batch-group">
              <div
                className="flex flex-wrap gap-4"
                role="radiogroup"
                aria-labelledby="certificateBatchTrainingHeading"
              >
                <div className="flex align-items-center gap-2">
                  <RadioButton
                    inputId="certificateBatchScopeItems"
                    name="certificateBatchScope"
                    value="items"
                    checked={!allTraining}
                    onChange={() => setAllTraining(false)}
                  />
                  <label htmlFor="certificateBatchScopeItems">
                    {message("certificate_batch_scope_items")}
                  </label>
                </div>
                <div className="flex align-items-center gap-2">
                  <RadioButton
                    inputId="certificateBatchScopeAll"
                    name="certificateBatchScope"
                    value="all"
                    checked={allTraining}
                    onChange={() => setAllTraining(true)}
                  />
                  <label htmlFor="certificateBatchScopeAll">
                    {message("certificate_batch_scope_all")}
                  </label>
                </div>
              </div>
              {(allTraining && (
                <p className="certificate-batch-hint m-0">
                  {message("certificate_batch_scope_all_hint")}
                </p>
              )) ||
                null}
            </div>
            {(!allTraining && (
              <div className="certificate-batch-group">
                <MultiSelectInput
                  input={{
                    name: "certificateBatchItems",
                    value: chosen,
                    onChange: (value) => setChosen(value || NO_ITEMS),
                    "aria-label": message("certificate_batch_items_label"),
                  }}
                  placeholder={message("certificate_batch_items_placeholder")}
                  options={options}
                  optionLabel="label"
                  loading={itemsRequesting}
                  filter
                  showAsTags={false}
                />
                {(noItems && (
                  <p className="certificate-batch-hint m-0">
                    {message("certificate_batch_no_items")}
                  </p>
                )) ||
                  null}
              </div>
            )) ||
              null}
            <div className="edexlms-field">
              <span
                id="certificateBatchTypeLabel"
                className="edexlms-field-label block"
              >
                {message("certificate_batch_training_type_label")}
              </span>
              <SelectButton
                value={trainingType}
                options={trainingTypeOptions}
                onChange={(e) => e.value && setTrainingType(e.value)}
                allowEmpty={false}
                aria-labelledby="certificateBatchTypeLabel"
              />
            </div>
          </Section>

          <Section
            id="certificateBatchLearnersHeading"
            title={message("certificate_batch_learners")}
          >
            <MultiSelectInput
              input={{
                name: "certificateBatchAudiences",
                value: chosenAudiences,
                onChange: (value) => setChosenAudiences(value || NO_AUDIENCES),
                "aria-label": message("certificate_batch_audiences_label"),
              }}
              label={message("certificate_batch_audiences_label")}
              placeholder={message("certificate_batch_audiences_placeholder")}
              options={audienceOptions}
              optionLabel="name"
              loading={audiencesRequesting}
              filter
              showAsTags={false}
            />
            <div className="certificate-batch-group certificate-batch-filters">
              <UserFilters
                filters={userFilters}
                onFilterChange={setUserFilters}
              />
              {(userFilterLines.length > 0 && (
                <ul className="certificate-batch-filter-lines m-0 pl-3">
                  {userFilterLines.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              )) ||
                null}
              {(chosenAudiences.length === 0 &&
                userFilterLines.length === 0 && (
                  <p className="certificate-batch-hint m-0">
                    {message("certificate_batch_learners_hint")}
                  </p>
                )) ||
                null}
            </div>
          </Section>

          <Section
            id="certificateBatchDatesHeading"
            title={message("certificate_batch_dates")}
          >
            <div className="certificate-batch-group">
              <div className="flex flex-column sm:flex-row gap-3">
                {renderDate(
                  "certificateBatchCompletedFrom",
                  "certificate_batch_completed_from",
                  completedFrom,
                  setCompletedFrom,
                  { maxDate: completedTo || undefined }
                )}
                {renderDate(
                  "certificateBatchCompletedTo",
                  "certificate_batch_completed_to",
                  completedTo,
                  setCompletedTo,
                  { minDate: completedFrom || undefined }
                )}
              </div>
              <p className="certificate-batch-hint m-0">
                {message("certificate_batch_dates_hint")}
              </p>
            </div>
          </Section>
        </div>
      )) ||
        null}
    </Dialog>
  );
};

CertificateBatchDialog.propTypes = {
  visible: PropTypes.bool.isRequired,
  items: PropTypes.array,
  itemsType: PropTypes.number,
  itemsRequesting: PropTypes.bool,
  audiences: PropTypes.array,
  audiencesRequesting: PropTypes.bool,
  preview: PropTypes.object,
  previewRequesting: PropTypes.bool,
  previewError: PropTypes.string,
  creating: PropTypes.bool,
  createError: PropTypes.string,
  onHide: PropTypes.func.isRequired,
  loadItems: PropTypes.func,
  loadAudiences: PropTypes.func,
  loadPreview: PropTypes.func,
  resetPreview: PropTypes.func,
  createBatch: PropTypes.func,
};

const mapStateToProps = (state) => ({
  items: state.certificateBatches.items,
  itemsType: state.certificateBatches.itemsType,
  itemsRequesting: state.certificateBatches.itemsRequesting,
  audiences: state.certificateBatches.audiences || NO_AUDIENCES,
  audiencesRequesting: state.certificateBatches.audiencesRequesting,
  preview: state.certificateBatches.preview,
  previewRequesting: state.certificateBatches.previewRequesting,
  previewError: state.certificateBatches.previewError,
  creating: state.certificateBatches.creating,
  createError: state.certificateBatches.createError,
});

const mapDispatchToProps = (dispatch) => ({
  loadItems: (data) => dispatch(certificateBatchItemsRequest(data)),
  loadAudiences: () => dispatch(certificateBatchAudiencesRequest()),
  loadPreview: (data) => dispatch(certificateBatchPreviewRequest(data)),
  resetPreview: () => dispatch(certificateBatchPreviewReset()),
  createBatch: (data) => dispatch(certificateBatchCreateRequest(data)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(CertificateBatchDialog);
